import React, { useState, useEffect, useRef } from "react";
import { HotTable, HotColumn } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import { checkboxRenderer } from "handsontable/renderers";
import "handsontable/dist/handsontable.full.min.css";
import { AddCommaNumber } from "../../../helpers/Utils";

registerAllModules();

const prepareTableState = (products, stocks, recipes) => {
  const processedData = [];
  const mergeConfigs = [];
  const stockMap = new Map(stocks.map((s) => [s.id, s]));

  products.forEach((product) => {
    const startRow = processedData.length;
    const recipesForThisProduct = recipes.filter(
      (r) => r.product_id === product.id,
    );
    const isCurrentlyOneToOne =
      recipesForThisProduct.length === 1 &&
      recipesForThisProduct[0].stock?.name === product.name;

    // 1. Render các dòng dữ liệu thật
    recipesForThisProduct.forEach((recipe) => {
      if (!recipe.stock) return;
      const stock = recipe.stock;
      const stockInfoFromMasterList = stockMap.get(stock.id);
      const ingredientLabel = stockInfoFromMasterList
        ? stockInfoFromMasterList.name
        : stock.name;

      // *** SỬA LỖI 2: Xác định chính xác trạng thái isOneToOne cho dòng dữ liệu thật ***
      const isThisRecipeOneToOne = stock.name === product.name;

      processedData.push({
        productName: product.name,
        productId: product.id,
        ingredient: ingredientLabel,
        isOneToOne: isThisRecipeOneToOne, // Hiển thị trạng thái đúng
        unit: stock.unit,
        quantity: "",
        price: "",
        usageCount: recipe.count,
        inventory: stock.count,
        price_old: stock.price_new,
        isControlRow: false,
      });
    });

    // 2. Chỉ thêm dòng control nếu sản phẩm không phải là 1:1
    if (!isCurrentlyOneToOne) {
      processedData.push({
        productName: product.name,
        productId: product.id,
        ingredient: null,
        isOneToOne: false,
        unit: "",
        quantity: "",
        price: "",
        usageCount: "",
        inventory: "",
        price_old: "",
        isControlRow: true,
      });
    }

    const rowspan = processedData.length - startRow;
    if (rowspan > 1) {
      mergeConfigs.push({ row: startRow, col: 0, rowspan, colspan: 1 });
    }
  });

  return { data: processedData, merges: mergeConfigs };
};

// --- Component Chính ---
const RecipeManager = ({
  toggleOpenModalAddProducts,
  onSaveRecipe,
  data_products,
  data_stocks,
  make_products,
}) => {
  const hotRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [mergeConfig, setMergeConfig] = useState([]);

  const activeStocks = (data_stocks || []).filter(
    (stock) => stock.is_active !== false && stock.is_active !== "false",
  );
  const stockOptions = activeStocks.map((stock) => stock.name);
  const stockLabelToIdMap = new Map(
    data_stocks.map((stock) => [stock.name, stock.id]),
  );

  useEffect(() => {
    const { data, merges } = prepareTableState(
      data_products,
      data_stocks,
      make_products,
    );
    setTableData(data);
    setMergeConfig(merges);
  }, [data_products, data_stocks, make_products]);

  const handleAfterChange = (changes, source) => {
    if (
      !["edit", "CopyPaste.paste", "Autofill.fill"].includes(source) ||
      !changes
    ) {
      return;
    }

    const newData = tableData.map((row) => ({ ...row }));
    let structureChanged = false;

    changes.forEach(([row, prop, oldValue, newValue]) => {
      const rowObject = newData[row];
      if (!rowObject || !rowObject.isControlRow) return;

      // Cập nhật giá trị mới nhất vào bản sao
      rowObject[prop] = newValue;

      // Trigger 1: Người dùng nhập nguyên liệu (chắc chắn không phải 1:1)
      if (prop === "ingredient" && newValue && !rowObject.isOneToOne) {
        const stock = data_stocks.find((s) => s.name === newValue);
        rowObject.unit = stock ? stock.unit : "";
        rowObject.isControlRow = false;
        rowObject.quantity = rowObject.quantity || "";
        rowObject.price = rowObject.price || "";
        rowObject.usageCount = rowObject.usageCount || "";
        rowObject.inventory = rowObject.count || "";
        rowObject.price_old = rowObject.price_new || "";

        // Thêm dòng control mới
        const newControlRow = {
          productName: rowObject.productName,
          productId: rowObject.productId,
          ingredient: null,
          isOneToOne: false,
          unit: "",
          quantity: "",
          price: "",
          usageCount: "",
          isControlRow: true,
        };
        newData.splice(row + 1, 0, newControlRow);
        structureChanged = true;
      }

      // Trigger 2: Người dùng tick vào checkbox 1:1
      if (prop === "isOneToOne" && newValue === true) {
        const matchingStock = data_stocks.find(
          (s) => s.name === rowObject.productName,
        );
        rowObject.ingredient = matchingStock
          ? matchingStock.name
          : rowObject.productName;
        rowObject.unit = matchingStock ? matchingStock.unit : "";
        rowObject.isControlRow = false; // Biến thành dòng dữ liệu thật
        rowObject.usageCount = rowObject.usageCount || 1;
        rowObject.quantity = rowObject.quantity || 0;
        rowObject.price = rowObject.price || 0;
        rowObject.inventory = rowObject.count || "";
        rowObject.price_old = rowObject.price_new || "";

        // KHÔNG thêm dòng control mới ở đây
        structureChanged = true; // Vẫn cần tính toán lại merge
      }
    });

    if (structureChanged) {
      // Tính toán lại merge config
      const newMergeConfigs = [];
      let currentProductId = null;
      let startRow = -1;
      newData.forEach((rowItem, index) => {
        if (rowItem.productId !== currentProductId) {
          if (startRow !== -1) {
            const rowspan = index - startRow;
            if (rowspan > 1)
              newMergeConfigs.push({
                row: startRow,
                col: 0,
                rowspan,
                colspan: 1,
              });
          }
          currentProductId = rowItem.productId;
          startRow = index;
        }
      });
      if (startRow !== -1) {
        const rowspan = newData.length - startRow;
        if (rowspan > 1)
          newMergeConfigs.push({ row: startRow, col: 0, rowspan, colspan: 1 });
      }

      setTableData(newData);
      setMergeConfig(newMergeConfigs);
    } else {
      setTableData(newData);
    }
  };

  const handleSaveAll = () => {
    const dataToSave = tableData.filter(
      (row) => !row.isControlRow && row.usageCount > 0,
    );
    // console.log("Data to save:", dataToSave);
    const payload = dataToSave.map((row) => {
      const stockId = stockLabelToIdMap.get(row.ingredient);
      // Logic xác định 1:1 cần dựa vào việc tên nguyên liệu có trùng tên sản phẩm không
      const isOneToOne =
        row.ingredient === row.productName ||
        data_stocks.find((s) => s.name === row.ingredient)?.name ===
          row.productName;
      return {
        productId: row.productId,
        isOneToOne,
        ingredientValue: row.ingredient,
        stockId,
        unit: row.unit,
        usageCount: row.usageCount,
        quantity: row.quantity,
        price: row.price,
      };
    });
    // console.log("Payload to save:", payload);
    onSaveRecipe(payload);
    toggleOpenModalAddProducts();
  };

  const readOnlyCellStyle = `
        .htReadOnly {
          background-color: #f2f2f2 !important;
        }
    `;

  return (
    <div className="excel-table-container w-100">
      <style>{readOnlyCellStyle}</style>
      <HotTable
        ref={hotRef}
        data={tableData}
        mergeCells={mergeConfig}
        rowHeaders={true}
        colHeaders={[
          "Tên sản phẩm",
          "Tên nguyên liệu",
          "Tỷ lệ 1:1",
          "Đơn vị tính",
          "Số lượng sử dụng",
          "Số lượng nhập",
          "Giá mới",
          "Tồn kho",
          "Giá cũ",
        ]}
        colWidths={[200, 200, 80, 100, 100, 100, 100, 100, 100]}
        height="auto"
        licenseKey="non-commercial-and-evaluation"
        afterChange={handleAfterChange}
        stretchH="all"
        contextMenu={["remove_row"]}
        rowHeights={40}
        minRows={10}
      >
        <HotColumn
          data="productName"
          readOnly={true}
          className="htMiddle htReadOnly"
        />
        <HotColumn
          data="ingredient"
          type="autocomplete"
          source={stockOptions}
          strict={false}
          className="htMiddle"
        />
        <HotColumn
          data="isOneToOne"
          type="checkbox"
          className="htCenter htMiddle"
        >
          {(instance, td, row, col, prop, value, cellProperties) => {
            const rowData = instance.getSourceDataAtRow(row);
            if (!rowData) return;

            // 1. Đối với các dòng dữ liệu thật
            if (!rowData.isControlRow) {
              if (rowData.isOneToOne) {
                // Nếu đúng là sản phẩm 1:1, hiển thị checkbox để người dùng biết
                checkboxRenderer(
                  instance,
                  td,
                  row,
                  col,
                  prop,
                  value,
                  cellProperties,
                );
              } else {
                // Nếu là một phần của công thức phức tạp, hiển thị dấu gạch ngang
                td.innerHTML = "-";
              }
              cellProperties.readOnly = true;
              return;
            }

            // 2. Đối với các dòng control (thêm mới)
            const productId = rowData.productId;
            const hasExistingIngredients = instance
              .getSourceData()
              .some(
                (item) => item.productId === productId && !item.isControlRow,
              );

            if (hasExistingIngredients) {
              // Nếu sản phẩm đã có công thức, hiển thị dấu gạch ngang thay vì checkbox
              td.innerHTML = "-";
              cellProperties.readOnly = true;
            } else {
              // Nếu sản phẩm chưa có gì, hiển thị checkbox cho người dùng tương tác
              checkboxRenderer(
                instance,
                td,
                row,
                col,
                prop,
                value,
                cellProperties,
              );
            }
          }}
        </HotColumn>
        <HotColumn data="unit" className="htCenter htMiddle" />
        <HotColumn
          data="usageCount"
          type="numeric"
          className="htCenter htMiddle"
          renderer={(instance, td, row, col, prop, value, cellProperties) => {
            const rowData = instance.getSourceDataAtRow(row);
            const unit = rowData && rowData.unit ? ` ${rowData.unit}` : "";
            td.innerHTML =
              value !== null && value !== undefined && value !== ""
                ? value + unit
                : "";
            td.className = "htCenter htMiddle";
            return td;
          }}
        />
        <HotColumn
          data="quantity"
          type="numeric"
          className="htCenter htMiddle"
        />
        <HotColumn data="price" type="numeric" className="htCenter htMiddle" />
        <HotColumn
          data="inventory"
          type="numeric"
          className="htCenter htMiddle htReadOnly"
          readOnly={true}
          renderer={(instance, td, row, col, prop, value, cellProperties) => {
            if (value !== null && value !== undefined && value !== "") {
              const num = parseFloat(value);
              td.innerHTML = isNaN(num) ? value : num.toFixed(2);
            } else {
              td.innerHTML = "";
            }
            td.className = "htCenter htMiddle htReadOnly";
            return td;
          }}
        />
        <HotColumn
          data="price_old"
          type="numeric"
          className="htCenter htMiddle htReadOnly"
          readOnly={true}
          renderer={(instance, td, row, col, prop, value, cellProperties) => {
            td.innerHTML =
              value !== null && value !== undefined && value !== ""
                ? AddCommaNumber(value)
                : "";
            td.className = "htCenter htMiddle htReadOnly";
            return td;
          }}
        />
      </HotTable>
      <div className="d-flex justify-content-center mt-3">
        <button onClick={handleSaveAll} className="btn btn-primary">
          Lưu tất cả
        </button>
      </div>
    </div>
  );
};

export default RecipeManager;
